import React, { ComponentProps, ReactNode, useCallback, useMemo, useState } from 'react';
import { InfinitePaginatedList, RenderListItemProps } from '@frontend/components';
import { InfiniteQueryResult, UnpackArray } from '@frontend/types';

type InfiniteListData = Record<'rows', any>;

type BaseInfinitePaginatedProps<T extends InfiniteListData> = Omit<
  ComponentProps<typeof InfinitePaginatedList>,
  'renderListItem' | 'renderListItem'
> & {
  infiniteQueryProps: InfiniteQueryResult<T>;
};
export interface GroupedInfiniteListProps<T extends InfiniteListData, G extends string>
  extends BaseInfinitePaginatedProps<T> {
  /** Function to derive the group label from each row item */
  groupBy: (item: UnpackArray<T['rows']>) => G;

  /**
   * Function to render the header for a group.
   * Receives:
   *  - group: the group label
   *  - isCollapsed: whether the group is currently collapsed
   *  - toggleGroup: callback to toggle the collapsed state of the group
   *  - isToggleDisabled: whether toggling is disabled (e.g., only one group exists)
   *  - groupIndex: the index of the group
   */
  renderGroupHeader: (args: {
    group: G;
    isCollapsed: boolean;
    toggleGroup: (group: G) => void;
    isToggleDisabled: boolean;
    groupIndex: number;
  }) => ReactNode;

  /** Function to render a single row within a group */
  renderItem: (args: RenderListItemProps<UnpackArray<T['rows']>>) => ReactNode;
}

const GroupedInfiniteList = <T extends InfiniteListData, G extends string>({
  infiniteQueryProps,
  groupBy,
  renderGroupHeader,
  renderItem,
  ...listProps
}: GroupedInfiniteListProps<T, G>) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<G, boolean>>>({});

  const allRows: UnpackArray<T['rows']>[] = useMemo(
    () => infiniteQueryProps.data?.pages.flatMap((p) => p.rows ?? []) ?? [],
    [infiniteQueryProps.data]
  );

  const groupLabels: G[] = useMemo(() => Array.from(new Set(allRows.map(groupBy))), [allRows, groupBy]);

  const isToggleDisabled = useCallback(
    (group: G) => {
      const isFetching = infiniteQueryProps.isFetching || infiniteQueryProps.isFetchingNextPage;

      /**
       * Toggling is disabled if:
       * 1. Data is still being fetched (`isFetching` or `isFetchingNextPage` is true).
       * 2. The current group is the last group in the list (`groupLabels.length - 1`) and not all records have been fetched.
       *    - This ensures that the user cannot collapse the last group unless all records for the group are fetched.
       */

      if (isFetching) return true;

      const currentGroupIndex = groupLabels.indexOf(group);

      // If the current group is the last group
      if (currentGroupIndex === groupLabels.length - 1) {
        // Check if all records for the last group have been fetched
        const hasMoreRecords = !!infiniteQueryProps.hasNextPage;
        return hasMoreRecords; // Disable toggling if there are more records to fetch
      }

      return false; // Enable toggling for other groups
    },
    [infiniteQueryProps.isFetching, infiniteQueryProps.isFetchingNextPage, infiniteQueryProps.hasNextPage, groupLabels]
  );

  const handleToggleGroup = useCallback(
    (group: G) => {
      if (isToggleDisabled(group)) return;
      setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    },
    [isToggleDisabled]
  );

  /**
   * Build a flat list of group headers and rows.
   * - Each group header is added first.
   * - Rows for the group are added only if the group is not collapsed.
   * This structure ensures that the list is compatible with `InfinitePaginatedList`.
   */
  const flatList = useMemo(() => {
    const content: any[] = [];

    groupLabels.forEach((group) => {
      // Add the group header
      content.push({ __header: true, group, key: `header:${group}` });

      // Add rows for the group if it is not collapsed
      if (!collapsedGroups[group]) {
        allRows
          .filter((row) => groupBy(row) === group)
          .forEach((row, rowIndex) => {
            content.push({ __header: false, row, key: `item:${group}:${rowIndex}` });
          });
      }
    });

    return content;
  }, [allRows, groupLabels, groupBy, collapsedGroups]);

  /**
   * Inject the `flatList` into a new `infiniteQueryProps` object.
   * This ensures that the `InfinitePaginatedList` receives the modified data.
   */
  const patchedQueryProps: InfiniteQueryResult<{ rows: any[] }> = useMemo(() => {
    if (!infiniteQueryProps.data) return infiniteQueryProps;

    return {
      ...infiniteQueryProps,
      data: {
        pages: [{ rows: flatList }],
        pageParams: infiniteQueryProps.data.pageParams,
      },
    };
  }, [infiniteQueryProps, flatList]);

  /**
   * Render the `InfinitePaginatedList` with the patched query props.
   * The `renderListItem` function dynamically determines whether to render a group header or a row.
   */
  return (
    <InfinitePaginatedList
      infiniteQueryProps={patchedQueryProps}
      renderListItem={({ index }) => {
        const item = flatList[index];

        if (item.__header) {
          // Render group header
          const { group } = item;
          const isCollapsed = Boolean(collapsedGroups[group as G]);
          const groupIndex = groupLabels.indexOf(group);

          return renderGroupHeader({
            group,
            isCollapsed,
            toggleGroup: handleToggleGroup,
            isToggleDisabled: isToggleDisabled(group),
            groupIndex,
          });
        } else {
          // Render group row
          const { row } = item;
          return renderItem({ listItem: row, index });
        }
      }}
      {...listProps}
    />
  );
};

export default React.memo(GroupedInfiniteList) as typeof GroupedInfiniteList;
